import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';

import {
  getCampaignLineCreativeIdsToRemove,
  getLineIdsUsingCollectionIds,
} from '../campaign-editor/utils/collection-id-utils';
import {
  flash,
  tapFlashError,
  tapFlashSuccess,
} from '../generic/flash-messages';
import api from '../REST/api';
import batchUpdate from '../utils/batch-update';
import createUiAction, { markUiAction } from '../utils/create-ui-action';
import transformForPatch from './transform-for-patch';

export const removeCreativeAssociation =
  (campaignLineId, campaignLineCreativeId) => dispatch => {
    return dispatch(
      markUiAction({
        type: removeCreativeAssociation.toString(),
        payload: api
          .destroyCampaignLineCreative({ id: campaignLineCreativeId })
          .then(tapFlashSuccess(dispatch, 'Creative removed'))
          .then(() => ({ campaignLineId, campaignLineCreativeId }))
          .catch(tapFlashError(dispatch)),
      }),
    );
  };

removeCreativeAssociation.toString = () =>
  'creatives/remove-creative-association';

export const removeCreativeAssociationFromProductsUsingCollectionIds =
  () => (dispatch, getState) => {
    const {
      campaignEditor: {
        campaign,
        campaignLinesById,
        campaignLineCreativesByLineId,
      },
    } = getState();

    const lineIdsToClean = getLineIdsUsingCollectionIds(
      campaign,
      campaignLinesById,
    );
    const campaignLineCreativeIdsToRemove = getCampaignLineCreativeIdsToRemove(
      campaignLineCreativesByLineId,
      lineIdsToClean,
    );

    if (!campaignLineCreativeIdsToRemove.length) {
      return Promise.resolve();
    }

    return dispatch(
      markUiAction({
        type: removeCreativeAssociationFromProductsUsingCollectionIds.toString(),
        payload: Promise.all(
          campaignLineCreativeIdsToRemove.map(id =>
            api.destroyCampaignLineCreative({ id }),
          ),
        )
          .then(() => lineIdsToClean)
          .then(tapFlashSuccess(dispatch, 'Creatives removed'))
          .catch(tapFlashError(dispatch)),
      }),
    );
  };

removeCreativeAssociationFromProductsUsingCollectionIds.toString = () =>
  'creatives/remove-creative-association-from-products-using-collection-ids';

export const assignCreatives =
  (selectedCampaignLines, selectedCreatives) => (dispatch, getState) => {
    const { campaignLineCreativesByLineId } = getState().campaignEditor;
    const payloads = flatten(
      selectedCampaignLines.map(cLine => {
        const cLId = cLine.value;
        const cLCreatives = campaignLineCreativesByLineId[cLId] || [];
        const assignedCreatives = cLCreatives.map(
          ({ creativeId }) => creativeId,
        );
        const unassignedCreatives = selectedCreatives.filter(
          creative => !assignedCreatives.includes(creative),
        );
        return unassignedCreatives.map(creativeId => ({
          creativeId,
          campaignLineId: cLId,
        }));
      }),
    );

    return dispatch(
      markUiAction({
        type: assignCreatives.toString(),
        payload: Promise.all(
          payloads.map(load => api.addCampaignLineCreative({ data: load })),
        ).then(
          tapFlashSuccess(dispatch, 'Creatives assigned successfully'),
          tapFlashError(dispatch),
        ),
      }),
    );
  };

assignCreatives.toString = () => 'creatives/assign-creatives';

export const initializeCreatives = createUiAction(
  'creatives/initialize',
  data => data,
);

export const loadCreatives =
  ({ opportunityId }) =>
  (dispatch, getState) => {
    const { campaignEditor } = getState();
    api.getCreatives({ search: { opportunityId } }).then(creatives => {
      dispatch(
        initializeCreatives({
          context: { opportunityId },
          creativesById: keyBy(creatives, 'id'),
          campaignLinesById: campaignEditor?.campaignLinesById, // In the opportunity level there are no campaignLines in redux hence the check.
        }),
      );
    }, tapFlashError(dispatch));
  };
loadCreatives.toString = () => 'creatives/load-creatives';

export const addCreatives = createUiAction(
  'creatives/add-creatives',
  creatives => creatives,
);

export const uploadCreatives = creatives => (dispatch, getState) => {
  const { campaignEditor } = getState();
  dispatch(
    addCreatives({
      creativesById: keyBy(creatives, 'id'),
      campaignLinesById: campaignEditor?.campaignLinesById,
    }),
  );
};

export const deleteCreative = id => dispatch =>
  dispatch(
    markUiAction({
      type: deleteCreative.toString(),
      payload: api
        .destroyCreative({ id })
        .then(
          tapFlashSuccess(dispatch, 'Creative deleted successfully'),
          tapFlashError(dispatch),
        ),
      meta: { id },
    }),
  );
deleteCreative.toString = () => 'creatives/delete-creative';

export const resizeCreativeImage = id => dispatch =>
  dispatch(
    markUiAction({
      type: resizeCreativeImage.toString(),
      payload: api
        .resizeCreativeImage(id)
        .then(
          tapFlashSuccess(dispatch, 'Creative resized successfully'),
          tapFlashError(dispatch),
        ),
    }),
  );
resizeCreativeImage.toString = () => 'creatives/resize-creative-image';

export const updateCreative = createUiAction(
  'creatives/update-creative',
  (id, updates) => ({ id, updates }),
);

export const saveAllCreatives = () => (dispatch, getState) => {
  const {
    creativeAssets: { creativesById },
  } = getState();

  dispatch(flash('Saving creatives…', 'info'));

  const creativesToSave = Object.values(creativesById).filter(
    creative => creative.edited,
  );
  const action = creative =>
    api.patchCreative({
      id: creative.id,
      data: transformForPatch(creative),
    });
  return dispatch(
    markUiAction({
      type: saveAllCreatives.toString(),
      payload: batchUpdate(creativesToSave, 5, action).then(
        tapFlashSuccess(dispatch, 'Creatives updated successfully'),
        tapFlashError(dispatch),
      ),
    }),
  );
};

saveAllCreatives.toString = () => 'creatives/save-all-creatives';
